import {
  discardAllErrors,
  patchAuthFlow,
  replaceSession,
  reportError,
  resetAuthFlow,
} from '../state/app-state';
import * as accountService from '../services/account';
import * as auth from '../services/authentication';
import { getSession } from '../services/authentication';
import { apolloClient } from '../services/graphql-client';
import * as honeybadger from '../services/honeybadger';
import { Session } from '../services/models/session';
import { SignUpRequest } from '../services/models/signup-request';
import * as plausible from '../services/plausible';
import * as segment from '../services/segment';
import { discardLocalSession } from '../services/authentication';
import { onUserSignOut } from '../services/user-events';
import { resetIdentifiedViewer } from './useAccount';
import { minutesBetween } from '../utils/date';

// Auth flows, previously the session-actions thunks. Analytics events are
// preserved verbatim from the redux-segment middleware payloads.

const completeSignIn = (session: Session, memberSinceMinutes?: number) => {
  replaceSession(session);
  patchAuthFlow({ isFetching: false, isAccountDeactivated: false });
  segment.identifyById(session.account.userid);
  segment.trackEvent(
    typeof memberSinceMinutes !== 'undefined' && memberSinceMinutes < 5
      ? 'User Signed Up'
      : 'User Signed In'
  );
};

// The backend (AccountServiceImpl#handleSignin) has exactly two user-facing
// accountSignin failure messages: "Looks like we had trouble logging you
// in." for a wrong password, and "Invalid credentials" for every other
// rejected sign-in (unknown email, deactivated account, missing password
// hash, email/account mismatch). There is no error code or extensions field
// to key off instead: the GraphQL layer here never sets a custom
// classification, so every thrown exception — expected or not — surfaces
// with the generic graphql-java default `extensions.classification:
// "DataFetchingException"` (verified against a live accountSignin error
// response). These two literals are the backend's complete, stable set of
// user-facing sign-in copy; matching on them is as precise as the backend
// currently allows. This suppression is scoped to handleAuthError alone, so
// a false match here only ever hides an auth-flow error from Honeybadger —
// it can't affect reporting anywhere else in the app.
const EXPECTED_SIGNIN_FAILURES = [
  'Looks like we had trouble logging you in',
  'Invalid credentials',
];

// The same error funnel the legacy asyncAction wrapper applied.
const handleAuthError = (error: unknown, onError?: () => void) => {
  console.error(error);

  const errorMessage = error instanceof Error ? error.message : 'Unknown Error';

  // Expected user errors, not app failures — keep them out of Honeybadger
  // but still surface the message on the form.
  if (!EXPECTED_SIGNIN_FAILURES.some(message => errorMessage.includes(message))) {
    honeybadger.notify(error, 'auth');
  }

  if (errorMessage.endsWith('Access denied')) {
    discardLocalSession();
    onUserSignOut();
    window.location.reload();
  } else {
    if (error instanceof Error) reportError(error);
    onError?.();
  }
};

export const requestSignIn = async (email: string, password: string) => {
  patchAuthFlow({ isFetching: true });
  try {
    const session = await auth.signIn(email, password);
    plausible.trackEvent('Signed In', { mode: 'Email' });
    completeSignIn(session);
  } catch (error) {
    handleAuthError(error, () => patchAuthFlow({ isFetching: false }));
  }
};

export const requestSignUp = async (signUpRequest: SignUpRequest) => {
  patchAuthFlow({ isFetching: true });
  try {
    const session = await auth.signUp(signUpRequest);
    plausible.trackEvent('Signed Up', { mode: 'Email' });
    completeSignIn(session);
  } catch (error) {
    handleAuthError(error, () => patchAuthFlow({ isFetching: false }));
  }
};

export const verifyLogin = async (token: string) => {
  patchAuthFlow({ isFetching: true });
  try {
    const session = await auth.loginVerification(token);
    completeSignIn(session);
  } catch (error) {
    handleAuthError(error, () => patchAuthFlow({ isFetching: false }));
  }
};

export const signInWithTwitter = async (
  oauthToken: string,
  oauthVerifier: string,
  hasSession: boolean
) => {
  patchAuthFlow({ isFetching: true });
  try {
    const session = await auth.signInWithTwitter(oauthToken, oauthVerifier, hasSession);
    // No analytics here — the opener window finalizes the sign-in.
    replaceSession(session);
    patchAuthFlow({ isFetching: false });
  } catch (error) {
    handleAuthError(error, () => patchAuthFlow({ isFetching: false }));
  }
};

export const finalizeSignInWithTwitter = async () => {
  try {
    const session = getSession();

    if (session && session.account && session.token) {
      const memberSinceInMinutes = minutesBetween(
        new Date(),
        new Date(session.account.memberSince)
      );

      plausible.trackEvent(memberSinceInMinutes < 5 ? 'Signed Up' : 'Signed In', {
        mode: 'Twitter',
      });

      completeSignIn(session, memberSinceInMinutes);
    }
  } catch (error) {
    handleAuthError(error, () => patchAuthFlow({ isFetching: false }));
  }
};

export const completeSignOut = () => {
  replaceSession(null);
  resetAuthFlow();
  discardAllErrors();
  segment.trackEvent('User Signed Out');
  segment.reset();
  resetIdentifiedViewer();
  // Server state (account, flags, products, briefs details) lives in the
  // page-lifetime Apollo cache — drop it on every sign-out path so nothing
  // from this account can be served to the next sign-in on this tab.
  void apolloClient.clearStore().catch(() => undefined);
};

export const signOut = async () => {
  try {
    await auth.signOut();
    completeSignOut();
  } catch (error) {
    handleAuthError(error);
  }
};

export const applyActivatedSession = (session: Session) => {
  replaceSession(session);
  patchAuthFlow({ isFetching: false });
};

export const requestPasswordReset = async (email: string) => {
  patchAuthFlow({ isRequestingPasswordReset: true });
  try {
    const success = await auth.requestPasswordReset(email);
    if (success) {
      patchAuthFlow({ isRequestingPasswordReset: false, isPasswordResetRequested: true });
    } else {
      patchAuthFlow({ isRequestingPasswordReset: false });
    }
  } catch (error) {
    handleAuthError(error, () => patchAuthFlow({ isRequestingPasswordReset: false }));
  }
};

export const discardPasswordResetRequestNotification = () => {
  patchAuthFlow({ isPasswordResetRequested: false });
};

export const resetPassword = async (code: string, password: string) => {
  patchAuthFlow({ isResettingPassword: true });
  try {
    const success = await auth.resetPassword(code, password);
    if (success) {
      patchAuthFlow({ isResettingPassword: false, isPasswordResetConfirmed: true });
    } else {
      patchAuthFlow({ isResettingPassword: false });
    }
  } catch (error) {
    handleAuthError(error, () => patchAuthFlow({ isResettingPassword: false }));
  }
};

export const discardPasswordResetNotification = () => {
  patchAuthFlow({ isPasswordResetConfirmed: false });
};

// Convenience re-exports so components have a single import site.
export { useAuthFlow, useSession } from '../state/app-state';
